import {
  Box,
  Container,
  Grid,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Link as RouterLink } from 'react-router-dom';

const cohortSummary = [
  ['264', 'healthy endothelial RNA-seq samples'],
  ['63', 'public studies'],
  ['16', 'endothelial tissues, including Brain'],
  ['2', 'complementary differential-expression methods'],
];

const dataDestinations = [
  {
    title: 'Transmembrane Focus',
    to: '/transmembrane',
    description: 'Review RRA-prioritized transmembrane genes ordered by the four-list RRA ranking with method-specific, bootstrap, proteomic, and expression evidence.',
  },
  {
    title: 'All genes',
    to: '/all-genes?method=deseq2&direction=up&scope=all',
    description: 'Examine and export method-, direction-, and tissue-scope-specific differential-expression results.',
  },
];

const sectionSx = {
  py: { xs: 4, md: 5 },
  borderTop: '1px solid',
  borderColor: 'divider',
};

export default function HomePage() {
  return (
    <>
      <Box
        component="section"
        aria-labelledby="home-heading"
        sx={{ py: { xs: 4, md: 5 }, bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={{ xs: 4, md: 5 }} alignItems="stretch">
            <Grid item xs={12} md={7} sx={{ display: 'flex' }}>
              <Box component="header" sx={{ maxWidth: 780, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <Typography id="home-heading" component="h1" variant="h2" sx={{ fontSize: { xs: '2.6rem', md: '3.35rem' }, mb: 1 }}>
                  Brendo
                </Typography>
                <Typography component="p" variant="h5" color="primary.dark" sx={{ mb: 2.5 }}>
                  Brain Endothelial Open Resource
                </Typography>
                <Typography color="text.secondary" sx={{ fontSize: '1.08rem', lineHeight: 1.7 }}>
                  Brendo is a reference database for genes differentially expressed in brain endothelial cells, with an emphasis on genes encoding proteins with transmembrane helices. It integrates pairwise endothelial RNA-seq comparisons, Robust Rank Aggregation, bootstrap validation, protein evidence, and expression context to support blood–brain barrier research.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
              <Box component="section" aria-labelledby="cohort-heading" sx={{ width: '100%', p: { xs: 3, md: 3.5 }, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography id="cohort-heading" component="h2" variant="h4" sx={{ mb: 1.25 }}>
                  Cohort and analysis scope
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.65 }}>
                  The resource combines healthy bulk RNA-seq samples from public studies and compares Brain endothelium with endothelial samples from other tissues using DESeq2 and the Wilcoxon rank-sum test.
                </Typography>
                <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  {cohortSummary.map(([value, label]) => (
                    <Box component="div" key={label} sx={{ minWidth: 0, p: { xs: 2, sm: 2.25 }, display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography component="dt" variant="body2" color="text.secondary" sx={{ order: 2, mt: 0.75, lineHeight: 1.45 }}>{label}</Typography>
                      <Typography component="dd" variant="h4" sx={{ m: 0, order: 1, fontWeight: 700, lineHeight: 1.1 }}>{value}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="xl">
        <Box data-layout="landing-exploration" sx={{ py: { xs: 4, md: 5 } }}>
          <Box component="section" aria-labelledby="access-heading" sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography id="access-heading" component="h2" variant="h4" sx={{ mb: 2 }}>Access the data</Typography>
            <Grid container spacing={2} sx={{ flex: 1 }}>
              {dataDestinations.map((destination) => (
                <Grid item xs={12} sm={6} key={destination.title} sx={{ display: 'flex' }}>
                  <Link
                    component={RouterLink}
                    to={destination.to}
                    aria-label={destination.title}
                    underline="none"
                    sx={{ width: '100%', p: { xs: 2.5, md: 3 }, display: 'flex', flexDirection: 'column', color: 'text.primary', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2, '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' }, '&:focus-visible': { borderColor: 'primary.main', bgcolor: 'action.hover' } }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
                      <Typography component="h3" variant="h5" color="primary.dark">{destination.title}</Typography>
                      <ArrowForwardRoundedIcon aria-hidden="true" color="primary" />
                    </Box>
                    <Typography color="text.secondary" sx={{ mt: 1.25, lineHeight: 1.65 }}>{destination.description}</Typography>
                  </Link>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

        <Box component="section" aria-labelledby="acknowledgements-heading" sx={sectionSx}>
          <Typography id="acknowledgements-heading" component="h2" variant="h4" sx={{ mb: 2.5 }}>
            Acknowledgements and affiliations
          </Typography>
          <Grid container spacing={{ xs: 3, md: 5 }} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack spacing={2}>
                <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  This resource was created with the efforts of Sabancı University faculty, particularly with contributions from <Link href="https://adebalilab.org/" target="_blank" rel="noreferrer">Ogün Adebali</Link> and <Link href="https://mustafaoglulab.com/" target="_blank" rel="noreferrer">Nur Mustafaoğlu</Link>. We thank <Link href="https://tfguclu.github.io/" target="_blank" rel="noreferrer">Tandaç Furkan Güçlü</Link> and <Link href="https://zeynepkilinc.github.io/" target="_blank" rel="noreferrer">Zeynep Kılınç</Link> for their input as the first testers of Brendo.
                </Typography>
                <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  Brendo is part of the “Determination of transcytosis proteins in cerebral capillaries by computational and experimental methods to carry drugs to the brain” project, supported by the Scientific and Technological Research Council of Türkiye (TÜBİTAK) 1001 program.
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box
                component="img"
                src="/images/home_banner.png"
                alt="Adebali Lab, Mustafaoglu Lab, and Sabancı University"
                sx={{ display: 'block', width: '100%', maxWidth: 650, height: 'auto', mx: 'auto' }}
              />
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
}
